import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type {
  FailedCategory,
  FailureCategoryR,
  JobState,
} from '../api/types';

interface UseJobsFilters {
  state?: JobState[] | undefined;
  search?: string | undefined;
  tag?: string | undefined;
  station?: string | undefined;
  /** Phase 6.5 A — drill-down from the BatchCard «ОШИБКИ» block. */
  failed_category?: FailedCategory | undefined;
  /** Phase R — drill-down from the «к разбору» counter / popover. Uses
   *  Job.failure_category (indexed membership). Repeatable (OR) so the
   *  combined «к разбору» list can pass service_failed + other at once.
   *  Independent of ``failed_category`` (V2 taxonomy via post-filter). */
  failure_category?: FailureCategoryR[] | undefined;
  /** Phase 6.5 B — drill-down from «N без эталона» badge. */
  residual_skip?: boolean | undefined;
  /** Phase 6.5 C — drill-down from a per-config row click. Carries
   *  the canonical key (`"gps"` / `"beidou,galileo,glonass,gps"`). */
  config?: string | undefined;
  limit?: number;
}

/** Poll ``/jobs`` with adaptive frequency:
 *
 *   * 5 s when any job in the current page is ``pending`` or ``submitted``
 *     — those are the rows where the user wants to see progress.
 *   * 30 s otherwise — page is fully settled (DONE/FAILED/CANCELLED),
 *     so we don't burn battery / API quota on idle refreshes.
 *
 *  Read from ``query.state.data`` (React-Query 5 idiom) rather than a
 *  parallel React state, so we don't introduce double sources of truth.
 */
export function useJobs(filters: UseJobsFilters) {
  return useQuery({
    queryKey: ['jobs', filters],
    queryFn: () => api.listJobs(filters),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      const hasActive = data.items.some(
        (j) => j.state === 'pending' || j.state === 'submitted',
      );
      return hasActive ? 5000 : 30000;
    },
    staleTime: 2000,
  });
}
